import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import type { PayrollListItem } from "@/modules/hr/payroll/domain/types";
import { RowActions } from "@/shared/ui/RowActions";

const statusLabel: Record<PayrollListItem["status"], string> = {
  draft: "ຮ່າງ",
  confirmed: "ຢືນຢັນ",
  paid: "ຊຳລະແລ້ວ",
};

type PayrollByPeriodTableProps = {
  data: PayrollListItem[];
  isLoading?: boolean;
  isError?: boolean;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange?: (field: string, desc: boolean) => void;
  onDetail: (row: PayrollListItem) => void;
  onSlip: (row: PayrollListItem) => void;
};

export function PayrollByPeriodTable({
  data,
  isLoading,
  isError,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onDetail,
  onSlip,
}: PayrollByPeriodTableProps) {
  const columns: TanstackReactTable.ColumnDef<PayrollListItem>[] = [
    createSortableColumn<PayrollListItem>("employeeName", "ພະນັກງານ", {
      size: 200,
      cell: ({ row }) => <span className="font-medium">{row.original.employeeName}</span>,
    }),
    createSortableColumn<PayrollListItem>("netSalary", "ສຸດທິ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-right tabular-nums">
          {row.original.netSalary.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    }),
    createSortableColumn<PayrollListItem>("status", "ສະຖານະ", {
      size: 100,
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.status === "paid"
              ? "default"
              : row.original.status === "confirmed"
                ? "secondary"
                : "outline"
          }
        >
          {statusLabel[row.original.status]}
        </Badge>
      ),
    }),
    {
      id: "actions",
      header: "ການກະທຳ",
      cell: ({ row }) => (
        <RowActions
          maxInline={2}
          actions={[
            { label: "ລາຍລະອຽດ", onClick: () => onDetail(row.original), variant: "outline" },
            { label: "ໃບ slip", onClick: () => onSlip(row.original) },
          ]}
        />
      ),
      meta: { align: "right" },
      size: 160,
    },
  ];

  if (isError) {
    return (
      <div className="rounded-xl border bg-card p-8 text-center text-destructive">
        ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນ.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <DataTable<PayrollListItem, unknown>
        noDataMessage="ບໍ່ມີຂໍ້ມູນເງິນເດືອນໃນເດືອນນີ້."
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={0}
        limit={50}
        totalCount={totalCount}
        onPaginationChange={() => {}}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={
          onSortingChange
            ? (sorting) => {
                const s = sorting[0];
                if (s?.id) onSortingChange(s.id, !!s.desc);
              }
            : undefined
        }
      />
    </div>
  );
}
